import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Divider,
  Button,
  IconButton,
  Paper,
  Modal,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { uploadImage } from "services/imageUpload";
import { IMAGE_URL } from "network";

const ProcedureSection = ({
  procedures = [],
  onChange,
  deletedFiles = [],
  setDeletedFiles,
  error,
  touched,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [newProcedure, setNewProcedure] = useState({
    title: "",
    description: "",
    image: "",
  });
  const [previewProcedureImage, setPreviewProcedureImage] = useState("");
  const [editIndex, setEditIndex] = useState(null); // NEW: Track which index is being edited
  const sliderRef = useRef(null);

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 600, settings: { slidesToShow: 1 } },
    ],
  };

  const handleAddProcedure = () => {
    if (editIndex !== null) {
      // Editing existing procedure
      const updated = procedures.map((p, i) =>
        i === editIndex ? newProcedure : p
      );
      onChange(updated);
    } else {
      // Adding new procedure
      const updated = [...procedures, newProcedure];
      onChange(updated);
    }
    setOpenModal(false);
    setNewProcedure({ title: "", description: "", image: "" });
    setPreviewProcedureImage("");
    setEditIndex(null);
  };

  const handleDeleteProcedure = (index) => {
    const updated = procedures.filter((_, i) => i !== index);
    onChange(updated);
  };

  const delmedia = async (image) => {
    setDeletedFiles(image);
  };

  const handleEditProcedure = (index) => {
    setEditIndex(index);
    const proc = procedures[index];
    setNewProcedure(proc);
    setPreviewProcedureImage(IMAGE_URL + proc.image);
    setOpenModal(true);
  };

  useEffect(() => {
    if (procedures.length > 0 && sliderRef.current) {
      sliderRef.current.slickGoTo(procedures.length - 1);
    }
  }, [procedures]);

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" gutterBottom>
        Procedure*
      </Typography>
      <Box sx={{ py: 2 }}>
        <Slider ref={sliderRef} {...sliderSettings}>
          {procedures.map((p, index) => (
            <div key={index}>
              <Paper sx={{ mx: 1, p: 2, position: "relative" }}>
                <IconButton
                  onClick={() => handleEditProcedure(index)}
                  sx={{ position: "absolute", top: 8, right: 40 }}
                >
                  <Edit color="primary" />
                </IconButton>
                <IconButton
                  onClick={() => handleDeleteProcedure(index)}
                  sx={{ position: "absolute", top: 8, right: 8 }}
                >
                  <Delete color="error" onClick={() => delmedia(p.image)} />
                </IconButton>

                <img
                  crossorigin="anonymous"
                  src={IMAGE_URL + p.image}
                  alt={p.title}
                  onError={(e) => (e.target.src = "/fallback.jpg")}
                  style={{ width: "100%", height: 100, objectFit: "cover" }}
                />
                <Typography variant="subtitle1" fontWeight="bold" mt={1}>
                  {p.title}
                </Typography>
                <Typography variant="body2">{p.description}</Typography>
              </Paper>
            </div>
          ))}
          <div>
            <Paper
              onClick={() => {
                setEditIndex(null);
                setNewProcedure({ title: "", description: "", image: "" });
                setPreviewProcedureImage("");
                setOpenModal(true);
              }}
              sx={{
                mx: 1,
                height: 200,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                border: "2px dashed #ccc",
              }}
            >
              <Add fontSize="large" />
              <Typography>Add Procedure</Typography>
            </Paper>
          </div>
        </Slider>
      </Box>

      {/* Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" mb={2}>
            {editIndex !== null ? "Edit Procedure" : "Add Procedure"}
          </Typography>

          <TextField
            label="Title"
            value={newProcedure.title}
            onChange={(e) =>
              setNewProcedure((prev) => ({
                ...prev,
                title: e.target.value,
              }))
            }
            fullWidth
            margin="normal"
          />
          <TextField
            label="Description"
            value={newProcedure.description}
            onChange={(e) =>
              setNewProcedure((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            fullWidth
            margin="normal"
          />
          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files[0];
              if (file) {
                const uploadedPath = await uploadImage(file);
                setNewProcedure((prev) => ({
                  ...prev,
                  image: uploadedPath,
                }));
                setPreviewProcedureImage(IMAGE_URL + uploadedPath);
              }
            }}
          />
          {previewProcedureImage && (
            <Box mt={2}>
              <img
                crossorigin="anonymous"
                src={previewProcedureImage}
                alt="preview"
                style={{ width: "100%", height: 100, objectFit: "cover" }}
              />
            </Box>
          )}
          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="outlined" onClick={() => setOpenModal(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddProcedure}
              disabled={
                !newProcedure.title ||
                !newProcedure.description ||
                !newProcedure.image
              }
            >
              {editIndex !== null ? "Update" : "Add"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {error && touched && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default ProcedureSection;
